/**
 * Captures MultiPack TWA wrapper version from launch URL query params
 * (`mp_wc` / `mp_wn` injected by LauncherActivity) into sessionStorage,
 * then strips them from the address bar.
 *
 * Presence of a stored versionCode is the only reliable TWA signal we use —
 * ordinary PWA/standalone browser sessions never set these params.
 */
const WC_KEY = 'pmms.twa.wc'
const WN_KEY = 'pmms.twa.wn'

export type TwaWrapperIdentity = {
  versionCode: number
  versionName: string
}

export function captureTwaWrapperFromUrl(): void {
  if (typeof window === 'undefined') return

  try {
    const url = new URL(window.location.href)
    const wcRaw = url.searchParams.get('mp_wc')
    const wnRaw = url.searchParams.get('mp_wn')
    if (!wcRaw || !/^\d+$/.test(wcRaw)) return

    const versionCode = Number(wcRaw)
    if (!Number.isFinite(versionCode) || versionCode < 1) return

    sessionStorage.setItem(WC_KEY, String(versionCode))
    sessionStorage.setItem(WN_KEY, wnRaw?.trim() || String(versionCode))

    url.searchParams.delete('mp_wc')
    url.searchParams.delete('mp_wn')
    const next = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(window.history.state, '', next)
  } catch {
    // Ignore — non-TWA or storage blocked.
  }
}

export function getInstalledTwaWrapper(): TwaWrapperIdentity | null {
  if (typeof window === 'undefined') return null
  try {
    const wcRaw = sessionStorage.getItem(WC_KEY)
    if (!wcRaw || !/^\d+$/.test(wcRaw)) return null
    const versionCode = Number(wcRaw)
    if (!Number.isFinite(versionCode) || versionCode < 1) return null
    const versionName = sessionStorage.getItem(WN_KEY) || String(versionCode)
    return { versionCode, versionName }
  } catch {
    return null
  }
}

export function isMultipackTwaSession(): boolean {
  return getInstalledTwaWrapper() !== null
}
